import { dbBaseUrl } from "./env";
import type { MonthlyBillingReport, AdminConfig } from "../types";

export { type AdminConfig } from "../types";

type RawProcessPickup = {
  process_id: number;
  title: string;
  pickup_event: string;
  pickup_last12: number;
  pickup_2025: number;
  pickup_2026_jan_apr: number;
  last_pickup: string;
};

function encodeValue(value: unknown) {
  if (Array.isArray(value)) {
    return `{${value
      .map((item) => `"${String(item).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`)
      .join(",")}}`;
  }

  if (value && typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

async function request<T>(path: string, params?: Record<string, unknown>, method: "GET" | "POST" = "GET", signal?: AbortSignal): Promise<T> {
  let url = path.startsWith("http") ? path : `${dbBaseUrl}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const init: RequestInit = { method, headers, signal };
  if (method === "GET") {
    if (params) {
      const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(encodeValue(value))}`)
        .join("&");
      if (query) url += `?${query}`;
    }
  } else if (params) {
    init.body = JSON.stringify(params);
  }

  const response = await fetch(url, init);
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message = typeof payload === "string" ? payload : payload?.message || payload?.error || `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  if (payload?.error) {
    throw new Error(payload.error.message || payload.error.msg2 || payload.error || "Unknown API error");
  }

  return payload as T;
}

function getResults<T>(data: unknown): T {
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if ("__r" in record) return record.__r as T;
    if ("results" in record) return record.results as T;
  }
  return data as T;
}

export async function loadIncludedProcesses(signal?: AbortSignal): Promise<RawProcessPickup[]> {
  const payload = await request<unknown>(
    "/rpc/run_sql",
    {
      sql: `
        WITH included AS (
          SELECT process_id, title, pickup_event, pickup_last12, pickup_2025, pickup_2026_jan_apr, last_pickup
          FROM (VALUES
            (146, 'DCE - Courrier d''entreprises', 'Created', 104959, 104398, 32838, '2026-05-26'::date),
            (287, 'DCE - Relevage de boîtes postales', 'Created', 30780, 33299, 9283, '2026-05-26'::date),
            (115, 'DCE - Desserte d''agences bancaires', 'Created', 30421, 30565, 9822, '2026-05-26'::date),
            (170, 'Livraison - Casier Judiciaire', 'Collecté', 23377, 22510, 8209, '2026-05-26'::date),
            (155, 'DCE - Fiches de collecte', 'Created', 8834, 9340, 2853, '2026-05-26'::date),
            (160, 'Duplicata de Nationalité', 'Collecté', 7008, 9246, 1954, '2026-05-26'::date),
            (432, 'EMS - Livraisons OMS-INH', 'Created', 1918, 1988, 740, '2026-05-27'::date),
            (118, 'EMS - Envois PNLS - Prélèvements', 'Created', 811, 690, 313, '2026-05-26'::date),
            (67, 'EMS - Envois PNLS - Résultats', 'Created', 339, 0, 339, '2026-05-26'::date),
            (92, 'EMS - Peace Corps', 'Created', 221, 281, 61, '2026-05-21'::date),
            (57, 'DCE - Envois PostMap', 'Collecté', 26, 0, 26, '2026-05-15'::date),
            (116, 'EMS - Contrôles PNLS', 'Created', 4, 2, 2, '2026-01-21'::date)
          ) AS v(process_id, title, pickup_event, pickup_last12, pickup_2025, pickup_2026_jan_apr, last_pickup)
        )
        SELECT * FROM included ORDER BY process_id
      `,
    },
    "POST",
    signal
  );

  return getResults<RawProcessPickup[]>(payload);
}

export async function loadMonthlyBillingData(
  signal: AbortSignal,
  year: number,
  month: number,
  role: "viewer" | "admin",
  password: string
): Promise<MonthlyBillingReport> {
  // Call the server-side function that verifies password and returns data
  const payload = await request<unknown>(
    "/rpc/spt_billing_report",
    {
      p_role: role,
      p_password: password,
      p_year: year,
      p_month: month,
    },
    "POST",
    signal
  );

  const result = getResults<{
    error?: string;
    year: number;
    month: number;
    monthLabel: string;
    processes: Array<{
      processId: number;
      processTitle: string;
      pickupEvent: string;
      dailyCounts: Array<{ date: string; count: number }>;
      monthlyTotal: number;
      amountFcfa: number;
    }>;
    grandTotalPickups: number;
    grandTotalFcfa: number;
    config: { fixedBaseFcfa: number; pricePerPickupFcfa: number };
    availableMonths: Array<{ year: number; month: number; label: string }>;
  }>(payload);

  if (result.error) {
    throw new Error(result.error);
  }

  return result;
}

// Save admin config to localStorage (for process list management)
export async function saveAdminConfig(config: AdminConfig): Promise<void> {
  localStorage.setItem("spt-billing-admin-config", JSON.stringify(config));
}

export function loadAdminConfig(): AdminConfig | null {
  const stored = localStorage.getItem("spt-billing-admin-config");
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

export function getDefaultAdminConfig(): AdminConfig {
  return {
    processes: [
      { processId: 146, title: "DCE - Courrier d'entreprises", pickupEvent: "Created", isActive: true },
      { processId: 287, title: "DCE - Relevage de boîtes postales", pickupEvent: "Created", isActive: true },
      { processId: 115, title: "DCE - Desserte d'agences bancaires", pickupEvent: "Created", isActive: true },
      { processId: 170, title: "Livraison - Casier Judiciaire", pickupEvent: "Collecté", isActive: true },
      { processId: 155, title: "DCE - Fiches de collecte", pickupEvent: "Created", isActive: true },
      { processId: 160, title: "Duplicata de Nationalité", pickupEvent: "Collecté", isActive: true },
      { processId: 432, title: "EMS - Livraisons OMS-INH", pickupEvent: "Created", isActive: true },
      { processId: 118, title: "EMS - Envois PNLS - Prélèvements", pickupEvent: "Created", isActive: true },
      { processId: 67, title: "EMS - Envois PNLS - Résultats", pickupEvent: "Created", isActive: true },
      { processId: 92, title: "EMS - Peace Corps", pickupEvent: "Created", isActive: true },
      { processId: 57, title: "DCE - Envois PostMap", pickupEvent: "Collecté", isActive: true },
      { processId: 116, title: "EMS - Contrôles PNLS", pickupEvent: "Created", isActive: true },
      { processId: 55, title: "SPT - Passeports Capago", pickupEvent: "Created", isActive: true },
    ],
    billing: {
      fixedBaseFcfa: 300_000,
      pricePerPickupFcfa: 25,
    },
  };
}
